import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
// React 19 native metadata
import { Loader2, ArrowRight, Target, MessageSquare, Search, Shield, Zap, Swords, TrendingUp } from 'lucide-react';
import { useEssenceReward } from '../hooks/useEssenceReward';
import { useBreadcrumbSchema } from './common/useBreadcrumbSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIMENSIONS = [
  { icon: Target, label: 'Positioning', desc: 'Value proposition clarity' },
  { icon: MessageSquare, label: 'Messaging', desc: 'Copy & conversion focus' },
  { icon: Search, label: 'SEO & Content', desc: 'Discoverability & depth' },
  { icon: Shield, label: 'Trust & Proof', desc: 'Social proof & authority' },
  { icon: Zap, label: 'Conversion', desc: 'CTAs & user journey' },
  { icon: Swords, label: 'Competitive Edge', desc: 'Differentiation power' },
];

const StrategyScorePage = () => {
  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recentScores, setRecentScores] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { rewardEssence } = useEssenceReward();

  // SEO: BreadcrumbList JSON-LD for hub indexing
  useBreadcrumbSchema('strategy-score', useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'Strategy Score', path: '/strategy-score' },
  ], []));

  // Pre-fill URL from query param (coming from landing page)
  useEffect(() => {
    const prefill = searchParams.get('url');
    if (prefill) setUrl(prefill);
  }, [searchParams]);

  useEffect(() => {
    fetch(`${API_URL}/api/strategy-score/recent/list?limit=8`)
      .then(r => r.json())
      .then(d => setRecentScores(d.scores || []))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/strategy-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), email: email.trim() || null }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to analyze');
      }

      const data = await res.json();
      rewardEssence('strategy_score');
      navigate(`/score/${data.score_id}`);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="strategy-score-page">
      <>
        <title>Free AI Strategy Score — Rate Your Marketing | iSupplyAI</title>
        <meta name="description" content="Get your free AI-powered Strategy Score. Enter any URL and get scored across 6 dimensions: positioning, messaging, SEO, trust, conversion, and competitive edge." />
        <link rel="canonical" href="https://isupplyai.com/strategy-score" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="Free Strategy Score — AI Website Analysis | iSupplyAI" />
        <meta property="og:description" content="Get your free AI-powered Strategy Score. Enter any URL and get scored across 6 dimensions: positioning, messaging, SEO, trust, conversion, and competitive edge." />
        <meta property="og:url" content="https://isupplyai.com/strategy-score" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content="Free Strategy Score — AI Website Analysis | iSupplyAI" />
        <meta name="twitter:description" content="Get your free AI-powered Strategy Score. Enter any URL and get scored across 6 dimensions." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
      </>

      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="score-page-home-link">
            <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI" className="h-8 w-8 object-contain" />
            <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">iSupplyAI</span>
          </Link>
          <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">Back to App</Link>
        </div>
      </header>

      <section className="py-20 md:py-28 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-widest text-purple-400 uppercase mb-4">Free Tool</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Free AI Strategy Score — Rate Your Marketing in 60 Seconds
          </h1>
          <p className="text-lg text-gray-400 max-w-lg mx-auto mb-12">
            Enter any URL. Our AI analyzes your website across 6 critical strategy dimensions and gives you a shareable score card.
          </p>

          {/* Input Form */}
          <form onSubmit={handleSubmit} className="max-w-xl mx-auto mb-6" data-testid="score-form">
            <div className="flex flex-col gap-3">
              <div className="relative">
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Enter any website URL..."
                  required
                  disabled={loading}
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-base focus:outline-none focus:border-purple-500/50 transition-colors disabled:opacity-50"
                  data-testid="score-url-input"
                />
              </div>
              <div className="flex gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional — get tips to improve)"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-purple-500/50 transition-colors disabled:opacity-50"
                  data-testid="score-email-input"
                />
                <button
                  type="submit"
                  disabled={loading || !url.trim()}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                  data-testid="score-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Get Score
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
            {error && (
              <p className="mt-3 text-sm text-red-400" data-testid="score-error">{error}</p>
            )}
          </form>

          {loading && (
            <div className="mt-8 text-center" data-testid="score-loading-state">
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                <span className="text-sm text-purple-300">AI is analyzing your website across 6 dimensions...</span>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500 mt-4">No signup required. Results are instant and shareable.</p>
        </div>
      </section>

      {/* What We Score */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-base font-semibold text-gray-400 uppercase tracking-wider mb-8">
            6 Dimensions We Analyze
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="score-dimensions-grid">
            {DIMENSIONS.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-white/[0.03] border border-white/10 rounded-xl p-5 text-center hover:border-purple-500/30 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-5 h-5 text-purple-400" />
                </div>
                <p className="font-semibold text-white text-sm mb-1">{label}</p>
                <p className="text-xs text-gray-500">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Scores Leaderboard */}
      {recentScores.length > 0 && (
        <section className="pb-16 px-4" data-testid="score-leaderboard">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <h2 className="text-base font-semibold text-gray-400 uppercase tracking-wider">
                Recently Scored
              </h2>
            </div>
            <div className="grid gap-3">
              {recentScores.map((s) => {
                const gradeColor = s.grade?.startsWith('A') ? 'text-emerald-400 bg-emerald-500/15'
                  : s.grade === 'B' ? 'text-cyan-400 bg-cyan-500/15'
                  : s.grade === 'C' ? 'text-amber-400 bg-amber-500/15'
                  : 'text-red-400 bg-red-500/15';
                const domain = (() => {
                  try { return new URL(s.url?.startsWith('http') ? s.url : `https://${s.url}`).hostname.replace('www.', ''); }
                  catch { return s.url; }
                })();
                return (
                  <Link
                    key={s.score_id}
                    to={`/score/${s.score_id}`}
                    className="flex items-center justify-between px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl hover:border-purple-500/30 transition-all group"
                    data-testid={`score-recent-${s.score_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`shrink-0 text-sm font-bold w-10 h-10 rounded-lg flex items-center justify-center ${gradeColor}`}>
                        {s.grade}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-200 group-hover:text-white truncate transition-colors">
                          {domain}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{s.title || s.url}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      <span className="text-lg font-bold text-white">{s.overall_score}</span>
                      <span className="text-xs text-gray-500">/100</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Want to actually fix your score?</h2>
          <p className="text-gray-400 mb-6">
            iSupplyAI's Living War Room puts 4 AI strategists to work on your business.
            They debate, disagree, and deliver a strategy that works.
          </p>
          <Link
            to="/"
            className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all"
            data-testid="score-cta-button"
          >
            Try iSupplyAI Free
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 py-6 px-4">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <p>&copy; {new Date().getFullYear()} iSupplyAI, LLC. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StrategyScorePage;
